import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  publishingApi,
  type ActivityEntry,
  type ApprovalCenterItem,
  type DashboardSummary,
  type DeploymentPackage,
  type EnterpriseComparison,
  type PublishingQueueItem,
  type ReleaseNotes,
  type ReleaseNotification,
  type ReviewCenterItem,
} from '../../api/publishing';

const WORKSPACE = (getWorkspaceId() ?? '');

const SAMPLE_PACKAGE = {
  workspace_id: WORKSPACE,
  name: 'Summer Listing Update',
  marketplace: 'amazon',
  content: {
    type: 'listing',
    items: [
      {
        id: 'item-1',
        asin: 'B000DEMO01',
        sku: 'DEMO-SKU-001',
        fields: {
          title: 'Acme Premium Widget Tool Set — Professional Grade',
          description: 'Professional-grade widget tool for home and workshop use.',
          bullets: ['Durable steel construction', 'Ergonomic grip', 'Includes carry case'],
        },
      },
    ],
  },
};

export function PublishingDashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [reviewItems, setReviewItems] = useState<ReviewCenterItem[]>([]);
  const [approvals, setApprovals] = useState<ApprovalCenterItem[]>([]);
  const [queue, setQueue] = useState<PublishingQueueItem[]>([]);
  const [packages, setPackages] = useState<DeploymentPackage[]>([]);
  const [notifications, setNotifications] = useState<ReleaseNotification[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [selectedPkg, setSelectedPkg] = useState<string | null>(null);
  const [comparison, setComparison] = useState<EnterpriseComparison | null>(null);
  const [notes, setNotes] = useState<ReleaseNotes | null>(null);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'review' | 'approvals' | 'queue' | 'history'>('review');

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, review, appr, q, pkgs, act, notifs] = await Promise.all([
        publishingApi.dashboard(WORKSPACE),
        publishingApi.reviewCenter({ workspace_id: WORKSPACE }),
        publishingApi.approvalCenter(WORKSPACE),
        publishingApi.publishingQueue(WORKSPACE),
        publishingApi.listPackages(WORKSPACE),
        publishingApi.listActivity(20),
        publishingApi.listNotifications(true),
      ]);
      setDashboard(dash);
      setReviewItems(review.items);
      setApprovals(appr);
      setQueue(q);
      setPackages(pkgs);
      setActivity(act);
      setNotifications(notifs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load publishing dashboard');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createSampleRelease = async () => {
    setBusy(true);
    try {
      const pkg = await publishingApi.createPackage(SAMPLE_PACKAGE);
      await publishingApi.validateQuality(pkg.id);
      await publishingApi.createRelease({ package_id: pkg.id, workspace_id: WORKSPACE });
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const loadPackageDetail = async (id: string) => {
    setSelectedPkg(id);
    try {
      const [cmp, rn] = await Promise.all([
        publishingApi.compare(id),
        publishingApi.releaseNotes(id),
      ]);
      setComparison(cmp);
      setNotes(rn);
    } catch {
      setComparison(null);
      setNotes(null);
    }
  };

  const publishPackage = async (packageId: string) => {
    setBusy(true);
    try {
      await publishingApi.deploy({ package_id: packageId, mode: 'immediate' });
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const runSearch = async () => {
    if (!searchText.trim()) return;
    const result = await publishingApi.globalSearch({ text: searchText, workspace_id: WORKSPACE });
    setPackages(result.packages ?? []);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Release Management</h1>
          <p className="text-sm text-slate-500">
            Enterprise publishing, review, approval &amp; release pipeline (FCP-00007)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={createSampleRelease}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Create Sample Release
          </button>
          <button
            type="button"
            onClick={refresh}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Pending Releases', value: dashboard.pending_releases },
            { label: "Today's Releases", value: dashboard.today_releases },
            { label: 'Scheduled', value: dashboard.scheduled_releases },
            { label: 'Failed', value: dashboard.failed_releases, alert: dashboard.failed_releases > 0 },
            { label: 'Pending Approvals', value: dashboard.pending_approvals },
            { label: 'Queue Depth', value: dashboard.queue_depth },
            { label: 'Success Rate', value: `${dashboard.publishing_success_rate.toFixed(1)}%` },
            { label: 'Rollbacks', value: dashboard.rollback_count },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className={`mt-1 text-2xl font-semibold ${kpi.alert ? 'text-red-600' : 'text-slate-900'}`}>
                {kpi.value}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 border-b border-slate-200">
        {(['review', 'approvals', 'queue', 'history'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize ${
              tab === t ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t === 'review' ? 'Review Center' : t === 'approvals' ? 'Approval Center' : t === 'queue' ? 'Publishing Queue' : 'Packages'}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {tab === 'review' && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-4 py-3">
                <h2 className="font-medium text-slate-900">Pending Review</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {reviewItems.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-slate-500">No pending reviews</p>
                ) : (
                  reviewItems.map((item) => (
                    <button
                      key={item.release_id}
                      type="button"
                      onClick={() => loadPackageDetail(item.package_id)}
                      className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{item.release_code}</p>
                        <p className="text-xs text-slate-500">
                          {item.name} · {item.category} · {item.marketplace}
                        </p>
                      </div>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">{item.status}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {tab === 'approvals' && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-4 py-3">
                <h2 className="font-medium text-slate-900">Approval Center</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {approvals.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-slate-500">No pending approvals</p>
                ) : (
                  approvals.map((a) => (
                    <div key={a.approval_id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{a.release_code ?? a.package_id}</p>
                        <p className="text-xs text-slate-500">Level {a.level ?? 1} · {a.status}</p>
                      </div>
                      {a.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() =>
                            publishingApi
                              .bulkApprove({ approval_ids: [a.approval_id], reviewer_id: 'reviewer-1' })
                              .then(refresh)
                          }
                          className="rounded-lg bg-green-600 px-3 py-1 text-xs text-white hover:bg-green-700"
                        >
                          Approve
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {tab === 'queue' && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-4 py-3">
                <h2 className="font-medium text-slate-900">Publishing Queue</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {queue.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-slate-500">Queue is empty</p>
                ) : (
                  queue.map((q) => (
                    <div key={q.execution_id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{q.name}</p>
                        <p className="text-xs text-slate-500">{q.marketplace} · {q.mode}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{q.status}</p>
                        <p className="text-xs text-slate-500">{q.progress.toFixed(0)}%</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {tab === 'history' && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
                <input
                  type="search"
                  placeholder="Search releases, ASIN, SKU..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                />
                <button type="button" onClick={runSearch} className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm text-white">
                  Search
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {packages.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => loadPackageDetail(p.id)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{p.name}</p>
                      <p className="text-xs text-slate-500">{p.marketplace} · {p.content.type}</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{p.status}</span>
                      {p.status === 'preview' || p.status === 'approved' ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            publishPackage(p.id);
                          }}
                          className="rounded bg-indigo-600 px-2 py-0.5 text-xs text-white"
                        >
                          Publish
                        </button>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {selectedPkg && comparison && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Change Comparison</h3>
              <p className="mt-1 text-xs text-slate-500">
                {comparison.added} added · {comparison.changed} changed · {comparison.removed} removed
              </p>
              <div className="mt-3 space-y-2">
                {comparison.field_diffs.slice(0, 5).map((d) => (
                  <div key={d.field} className="rounded-lg bg-slate-50 p-2 text-xs">
                    <p className="font-medium text-slate-700">{d.field}</p>
                    <p className="text-red-600 line-through">{String(d.current ?? '—')}</p>
                    <p className="text-green-700">{String(d.proposed ?? '—')}</p>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                SEO Δ {comparison.seo_score_delta.toFixed(2)} · Quality {comparison.quality_score.toFixed(2)}
              </p>
            </div>
          )}

          {notes && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">AI Release Notes</h3>
              <p className="mt-1 text-xs text-slate-500">{notes.seo_improvement}</p>
              <ul className="mt-2 list-inside list-disc text-xs text-slate-600">
                {notes.published_changes.slice(0, 4).map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              {notes.expected_ctr != null && (
                <p className="mt-2 text-xs text-slate-500">
                  Expected CTR +{(notes.expected_ctr * 100).toFixed(1)}% · CVR +{((notes.expected_cvr ?? 0) * 100).toFixed(1)}%
                </p>
              )}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="font-medium text-slate-900">Notifications</h3>
            <div className="mt-2 space-y-2">
              {notifications.length === 0 ? (
                <p className="text-xs text-slate-500">No unread notifications</p>
              ) : (
                notifications.slice(0, 5).map((n) => (
                  <div key={n.id} className="rounded-lg bg-slate-50 p-2 text-xs">
                    <p className="font-medium">{n.title}</p>
                    <p className="text-slate-500">{n.body}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="font-medium text-slate-900">Recent Activity</h3>
            <div className="mt-2 space-y-2">
              {activity.slice(0, 8).map((a) => (
                <div key={a.id} className="text-xs text-slate-600">
                  <span className="font-medium text-slate-800">{a.action}</span> — {a.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
